import { PayloadAction, createReducer } from "@reduxjs/toolkit";
import {
    IAgentErrorPayload,
    IAgentMessage,
    agentConversationSend,
    agentConversationStart,
    agentConversationStop,
    agentConversationStream,
    agentLocalMessageSubmitted,
    closeAgentFeedback,
    openAgentFeedback,
    setAgentFeedback,
    submitAgentFeedback,
} from "../actions/agent";
import { logOut } from "../actions/user";

const AGENT_ERROR_GENERIC_KEY = "agent.error.generic";
const AGENT_ERROR_INTERNAL_KEY = "agent.error.internal";

export type IAgentToolStatus = {
    status: "inProgress" | "completed";
    name?: string;
};

export type IAgentResponseStatus = "idle" | "pending" | "failed";

export type IAgentState = {
    conversation_id?: string;
    message_ids: string[];
    messages: { [key: string]: IAgentMessage };
    reasoningSummaries: { [key: string]: string };
    messageErrors: { [key: string]: string };
    responseStatus: IAgentResponseStatus;
    activeUserMessageId?: string;
    activeAssistantMessageId?: string;
    agentStatus?: string;
    toolStatus?: IAgentToolStatus;
    streamError?: string;
    showFeedback: boolean;
    doneFeedback: boolean;
    feedback: string;
};

const getDefaultState = (): IAgentState => {
    return {
        message_ids: [],
        messages: {},
        reasoningSummaries: {},
        messageErrors: {},
        responseStatus: "idle",
        showFeedback: false,
        doneFeedback: false,
        feedback: undefined,
    };
};

const getAgentErrorMessageKey = (error?: IAgentErrorPayload): string => {
    switch (error?.code) {
        case "INTERNAL_ERROR":
            return AGENT_ERROR_INTERNAL_KEY;
        default:
            return AGENT_ERROR_GENERIC_KEY;
    }
};

const setAgentError = (
    state: IAgentState,
    error?: IAgentErrorPayload,
    targetMessageId?: string
): void => {
    const errorMessageKey = getAgentErrorMessageKey(error);
    const resolvedTargetMessageId =
        targetMessageId ??
        state.activeAssistantMessageId ??
        getMostRecentAssistantMessageIdAfterActiveUser(state);
    if (resolvedTargetMessageId) {
        state.messageErrors[resolvedTargetMessageId] = errorMessageKey;
        state.streamError = undefined;
    } else {
        state.streamError = errorMessageKey;
    }
    state.responseStatus = "failed";
    state.agentStatus = undefined;
    state.toolStatus = undefined;
};

const startAgentResponse = (
    state: IAgentState,
    message?: IAgentMessage
): void => {
    state.responseStatus = "pending";
    state.streamError = undefined;
    state.agentStatus = undefined;
    state.toolStatus = undefined;
    state.activeAssistantMessageId = undefined;
    if (message?.role === "user") {
        state.activeUserMessageId = message.message_id;
    }
};

const finishAgentResponse = (state: IAgentState): void => {
    state.responseStatus = "idle";
    state.agentStatus = undefined;
    state.toolStatus = undefined;
    state.streamError = undefined;
    state.activeAssistantMessageId = undefined;
    state.activeUserMessageId = undefined;
};

const trackAssistantMessage = (
    state: IAgentState,
    message: IAgentMessage
): void => {
    if (message.role === "assistant") {
        state.activeAssistantMessageId = message.message_id;
    }
};

const ensureAssistantMessage = (
    state: IAgentState,
    message: IAgentMessage
): void => {
    const assistantMessage: IAgentMessage = {
        ...message,
        role: message.role ?? "assistant",
        content: state.messages[message.message_id]?.content ?? "",
    };

    trackAssistantMessage(state, assistantMessage);
    if (!(assistantMessage.message_id in state.messages)) {
        insertMessageId(state, assistantMessage);
    }
    state.messages[assistantMessage.message_id] = {
        ...state.messages[assistantMessage.message_id],
        ...assistantMessage,
    };
};

const isAgentMessage = (payload?: unknown): payload is IAgentMessage =>
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as Partial<IAgentMessage>).message_id === "string";

const getAgentMessagePayload = (
    payload?: unknown
): IAgentMessage | undefined => {
    if (isAgentMessage(payload)) {
        return payload;
    }

    const wrappedMessage = (payload as { message?: unknown } | undefined)
        ?.message;
    return isAgentMessage(wrappedMessage) ? wrappedMessage : undefined;
};

const getMostRecentAssistantMessageIdAfterActiveUser = (
    state: IAgentState
): string | undefined => {
    const activeUserIndex = state.activeUserMessageId
        ? state.message_ids.indexOf(state.activeUserMessageId)
        : -1;
    const startIndex = activeUserIndex >= 0 ? activeUserIndex + 1 : 0;
    for (let i = state.message_ids.length - 1; i >= startIndex; i--) {
        const messageId = state.message_ids[i];
        if (state.messages[messageId]?.role === "assistant") {
            return messageId;
        }
    }
};

const insertMessageId = (state: IAgentState, message: IAgentMessage): void => {
    const messageId = message.message_id;
    if (state.message_ids.includes(messageId)) {
        return;
    }

    if (message.role === "assistant" && state.activeUserMessageId) {
        const activeUserIndex = state.message_ids.indexOf(
            state.activeUserMessageId
        );
        if (activeUserIndex >= 0) {
            state.message_ids.splice(activeUserIndex + 1, 0, messageId);
            return;
        }
    }

    state.message_ids.push(messageId);
};

const appendMessageContent = (
    state: IAgentState,
    message: IAgentMessage,
    content: string
) => {
    const messageId = message.message_id;
    trackAssistantMessage(state, message);
    delete state.messageErrors[messageId];
    if (messageId in state.messages) {
        state.messages[messageId].content =
            (state.messages[messageId].content ?? "") + content;
        state.messages[messageId] = {
            ...state.messages[messageId],
            ...message,
            content: state.messages[messageId].content,
        };
    } else {
        insertMessageId(state, message);
        state.messages[messageId] = {
            ...message,
            role: message.role ?? "assistant",
            content,
        };
    }
};

const mergeMessage = (
    state: IAgentState,
    action: PayloadAction<unknown>,
    append: boolean = false
) => {
    const message = getAgentMessagePayload(action.payload);
    if (!message) {
        return;
    }

    const messageId = message.message_id;
    trackAssistantMessage(state, message);
    if (message.role === "assistant" && message.content) {
        delete state.messageErrors[messageId];
    }
    if (messageId in state.messages) {
        if (append) {
            state.messages[messageId].content =
                (state.messages[messageId].content ?? "") +
                (message.content ?? "");
        } else {
            state.messages[messageId] = {
                ...state.messages[messageId],
                ...message,
                content: message.content ?? state.messages[messageId].content,
            };
        }
    } else {
        insertMessageId(state, message);
        state.messages[messageId] = message;
    }
};

const routeStreamUpdate = (
    state: IAgentState,
    action: PayloadAction<unknown>
) => {
    const update = getAgentMessagePayload(action.payload);
    if (!update) {
        finishAgentResponse(state);
        return;
    }

    const content = update.content ?? "";

    switch (update.event_type) {
        case "ASSISTANT_DELTA":
            state.responseStatus = "pending";
            appendMessageContent(state, update, content);
            state.agentStatus = "agent.finishStatus";
            state.toolStatus = undefined;
            state.streamError = undefined;
            return;
        case "PROGRESS":
            state.responseStatus = "pending";
            ensureAssistantMessage(state, update);
            state.agentStatus = content;
            state.toolStatus = undefined;
            state.streamError = undefined;
            return;
        case "TOOL_CALL_DELTA":
            state.responseStatus = "pending";
            ensureAssistantMessage(state, update);
            state.agentStatus = undefined;
            state.toolStatus = {
                status: "inProgress",
                name: update.metadata?.name || content || undefined,
            };
            state.streamError = undefined;
            return;
        case "TOOL_RESULT":
            state.responseStatus = "pending";
            ensureAssistantMessage(state, update);
            state.agentStatus = undefined;
            state.toolStatus = {
                status: "completed",
                name: update.metadata?.name || content || undefined,
            };
            state.streamError = undefined;
            return;
        case "REASONING_SUMMARY_DELTA":
            state.responseStatus = "pending";
            ensureAssistantMessage(state, update);
            state.reasoningSummaries[update.message_id] =
                (state.reasoningSummaries[update.message_id] ?? "") + content;
            return;
        case "COMPLETE":
            finishAgentResponse(state);
            return;
        case "ERROR":
            setAgentError(
                state,
                {
                    message: content,
                    code: update.metadata?.code,
                },
                update.message_id
            );
            return;
        default:
            if (update.role === "assistant" && update.token_index != null) {
                state.responseStatus = "pending";
                appendMessageContent(state, update, content);
            } else {
                mergeMessage(state, action);
            }
    }
};

export const agent = createReducer(getDefaultState(), (builder) => {
    builder.addCase(agentConversationStart.success, (state, action) => {
        state.conversation_id = action.payload?.conversation_id;
    });
    builder.addCase(agentConversationStop.success, () => {
        return getDefaultState();
    });
    builder.addCase(agentLocalMessageSubmitted, (state, action) => {
        startAgentResponse(state, action.payload);
        mergeMessage(state, action);
    });
    builder.addCase(agentConversationSend.request, (state, action) => {
        const message = getAgentMessagePayload(action.payload);
        if (message) {
            if (state.responseStatus !== "pending") {
                startAgentResponse(state, message);
            } else if (message.role === "user") {
                state.activeUserMessageId = message.message_id;
            }
            mergeMessage(state, action);
        }
    });
    builder.addCase(agentConversationSend.update, (state, action) => {
        mergeMessage(state, action);
    });
    builder.addCase(agentConversationSend.success, (state, action) => {
        mergeMessage(state, action);
        finishAgentResponse(state);
    });
    builder.addCase(agentConversationSend.error, (state, action) => {
        setAgentError(state, action.payload);
    });
    builder.addCase(agentConversationStream.request, (state, action) => {
        const message = getAgentMessagePayload(action.payload);
        if (message) {
            startAgentResponse(state, message);
            mergeMessage(state, action);
        }
    });
    builder.addCase(agentConversationStream.update, (state, action) => {
        routeStreamUpdate(state, action);
    });
    builder.addCase(agentConversationStream.success, (state, action) => {
        routeStreamUpdate(state, action);
        if (action.payload?.event_type !== "ERROR") {
            finishAgentResponse(state);
        }
    });
    builder.addCase(agentConversationStream.error, (state, action) => {
        setAgentError(state, action.payload);
    });
    builder.addCase(openAgentFeedback, (state) => {
        state.showFeedback = true;
        state.doneFeedback = false;
        state.feedback = null;
    });
    builder.addCase(closeAgentFeedback, (state) => {
        state.showFeedback = false;
        state.doneFeedback = false;
        state.feedback = null;
    });
    builder.addCase(setAgentFeedback, (state, action) => {
        state.feedback = action.payload;
    });
    builder.addCase(submitAgentFeedback.success, (state) => {
        state.doneFeedback = true;
    });
    builder.addCase(logOut.request, () => {
        return getDefaultState();
    });
});
